/**
 * What weather does to the world, run at 20 Hz in the overworld:
 *  - snowfall settles a snow layer and freezes surface water in cold biomes;
 *  - rain puts fires out and soaks farmland;
 *  - thunder strikes the ground: a bolt, a fire, damage and mob conversions.
 *
 * Vanilla ticks a random column of every loaded chunk (1/16 chance per tick for
 * precipitation); here a few random columns near the player stand in for that.
 * DOM-free: the game supplies the world and the strike effects.
 */

import {
  AIR, FARMLAND_DRY_ID, FARMLAND_ID, FIRE_ID, ICE_ID, IS_LAVA, IS_OPAQUE, IS_SOLID, IS_WATERY, SNOW_LAYER_ID, WATER_ID,
} from '../blocks';
import { CHUNK_SIZE } from '../constants';
import type { World } from '../world/chunk';
import type { MobType } from './types';
import { CHUNK_AREA } from '../world/chunk';
import { PRECIP_RAIN, PRECIP_SNOW } from '../worldgen/biomeClimate';
import { weather, type Weather } from './weather';

/** Columns examined per 20 Hz tick, within RANGE blocks of the player. */
const COLUMNS_PER_TICK = 6;
const RANGE = 48;
/** Vanilla: 1 / 100000 per loaded chunk per tick while thundering; chunks stand in for the player's surroundings. */
const STRIKE_CHUNKS = 81;
const STRIKE_CHANCE = 1 / 100000;
/** Vanilla lightning damage and reach. */
export const BOLT_DAMAGE = 5;
export const BOLT_RADIUS = 3;

/**
 * First free y above the highest solid / liquid block of a column (the rain
 * heightmap: roofs, leaves and water stop rain), or -1 when the chunk is not loaded.
 */
export function columnTop(world: World, x: number, z: number): number {
  const c = world.getChunk(x >> 4, z >> 4);
  if (!c) return -1;
  const base = (x & (CHUNK_SIZE - 1)) + (z & (CHUNK_SIZE - 1)) * CHUNK_SIZE;
  const blocks = c.blocks;
  for (let y = blocks.length / CHUNK_AREA - 1; y >= 0; y--) { // a short chunk (world/chunk.ts) is air above its arrays
    const id = blocks[base + y * CHUNK_AREA];
    if (IS_SOLID[id] || IS_OPAQUE[id] || IS_WATERY[id] || IS_LAVA[id]) return y + 1;
  }
  return 0;
}

/** Game hooks for the things a lightning bolt touches. */
export interface WeatherHost {
  world: World;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  /** Player position (x, z) to centre the random columns on. */
  focus(): { x: number; z: number };
  /** Strike at a block position: draw the bolt, play thunder, hurt and convert what is near. */
  strike(x: number, y: number, z: number): void;
}

export class WeatherEffects {
  /** Set false to stop strikes and ground effects (other dimensions). */
  enabled = true;

  constructor(private readonly host: WeatherHost, private readonly w: Weather = weather) {}

  /** One 20 Hz tick. */
  tick(): void {
    if (!this.enabled || !this.w.world) return;
    const w = this.w;
    if (w.rainLevel < 0.2) return;
    const f = this.host.focus();
    for (let i = 0; i < COLUMNS_PER_TICK; i++) {
      const x = Math.floor(f.x + (w.random() * 2 - 1) * RANGE);
      const z = Math.floor(f.z + (w.random() * 2 - 1) * RANGE);
      this.columnEffects(x, z);
    }
    if (w.isThundering() && w.random() < STRIKE_CHUNKS * STRIKE_CHANCE) {
      const x = Math.floor(f.x + (w.random() * 2 - 1) * 64);
      const z = Math.floor(f.z + (w.random() * 2 - 1) * 64);
      this.strikeAt(x, z);
    }
  }

  /** Precipitation on one column: snow, ice, extinguishing, soaking. */
  columnEffects(x: number, z: number): void {
    const w = this.w, host = this.host;
    const top = columnTop(host.world, x, z);
    if (top <= 0 || !host.canEdit(x, top, z)) return;
    const kind = w.precipAt(x, top, z);
    if (kind === PRECIP_SNOW) {
      const below = host.world.getBlock(x, top - 1, z);
      if (below === WATER_ID) {
        // vanilla freezeWater: a source with open neighbours, in the cold, away from light
        if (host.world.getBlockLight(x, top, z) < 10 && !this.surrounded(x, top - 1, z)) host.setBlock(x, top - 1, z, ICE_ID);
      } else if (host.world.getBlock(x, top, z) === AIR && below !== ICE_ID && IS_OPAQUE[below] && !IS_LAVA[below]
        && host.world.getBlockLight(x, top, z) < 10) {
        host.setBlock(x, top, z, SNOW_LAYER_ID);
      }
    } else if (kind === PRECIP_RAIN) {
      if (host.world.getBlock(x, top, z) === FIRE_ID) host.setBlock(x, top, z, AIR);
      for (let dy = 1; dy <= 2; dy++) {
        // farmland (under a crop the heightmap stops one block higher)
        if (host.world.getBlock(x, top - dy, z) === FARMLAND_DRY_ID) { host.setBlock(x, top - dy, z, FARMLAND_ID); break; }
      }
    }
  }

  /** Is the water block walled in by water on all four sides (vanilla leaves those liquid)? */
  private surrounded(x: number, y: number, z: number): boolean {
    const w = this.host.world;
    return IS_WATERY[w.getBlock(x + 1, y, z)] === 1 && IS_WATERY[w.getBlock(x - 1, y, z)] === 1
      && IS_WATERY[w.getBlock(x, y, z + 1)] === 1 && IS_WATERY[w.getBlock(x, y, z - 1)] === 1;
  }

  /** Lightning on a column: the bolt hits the top of the heightmap. */
  strikeAt(x: number, z: number): boolean {
    const host = this.host;
    const top = columnTop(host.world, x, z);
    if (top <= 0 || !host.canEdit(x, top, z)) return false;
    if (host.world.getBlock(x, top, z) === AIR && IS_SOLID[host.world.getBlock(x, top - 1, z)]) {
      // sets fire where it lands; rain puts it out again soon enough
      host.setBlock(x, top, z, FIRE_ID);
    }
    this.w.flash = 1;
    host.strike(x, top, z);
    return true;
  }
}

/** The mob a bolt turns another into (vanilla), or null. Types that do not exist yet stay unmapped. */
export function lightningConversion(type: string): MobType | null {
  return type === 'pig' ? 'zombified_piglin' : null;
}
